/**
 * DEMO clinical cases.
 *
 * ⚠️  EVERY RECORD HERE IS PLACEHOLDER DEVELOPMENT DATA. NONE OF IT DESCRIBES A
 *     REAL PATIENT, A REAL TREATMENT, OR A REAL OUTCOME.
 *
 * Two protections are deliberate and must not be "tidied away":
 *
 * 1. `consent: "unknown"` on every record. No documented patient consent exists
 *    for any clinical photography on this project, so `mayPublishCaseImages()`
 *    returns false for all of these and templates must not render patient
 *    imagery. Publishing identifiable patient images without consent is a
 *    medical-ethics and legal failure, not a content gap.
 * 2. Every title and narrative is prefixed with an explicit DEMO marker so it
 *    cannot be mistaken for a real case record by the client or by a visitor if
 *    one of these pages is ever briefly reachable.
 *
 * The narratives are written to demonstrate the SHAPE of a good case study — so
 * the template design is genuinely exercised and the client can see what to
 * supply — while stating plainly that they are illustrative.
 *
 * See docs/clinical-cases-demo-data.md for the replacement procedure.
 */

import type { ClinicalCase, ImageRef } from "@/content/types";

const DEMO_EN = "[DEMO]";
const DEMO_AR = "[بيانات تجريبية]";

/**
 * Placeholder image. Deliberately NOT a real clinic photograph: the existing
 * photos under /public/images may depict real patients whose consent status is
 * unknown, which is exactly the risk this dataset exists to avoid.
 */
const placeholderImage = (labelEn: string, labelAr: string): ImageRef => ({
  src: "/placeholder.svg",
  alt: {
    en: `${DEMO_EN} Placeholder image — ${labelEn}. No real patient photograph is used.`,
    ar: `${DEMO_AR} صورة بديلة — ${labelAr}. لا تُستخدم أي صورة لمريض حقيقي.`,
  },
  width: 1200,
  height: 1200,
  provenance: "demo",
});

export const CASES: readonly ClinicalCase[] = [
  {
    slug: "demo-single-tooth-implant",
    provenance: "demo",
    service: "dental-implants",
    doctor: "demo-implant-specialist",
    consent: "unknown",
    title: {
      en: `${DEMO_EN} Replacing a single missing back tooth`,
      ar: `${DEMO_AR} استبدال ضرس خلفي مفقود`,
    },
    concern: {
      en: `${DEMO_EN} Illustrative example. A patient who had lost one back tooth and found chewing on that side difficult.`,
      ar: `${DEMO_AR} مثال توضيحي. مريض فقد ضرسًا خلفيًا وأصبح المضغ على هذا الجانب صعبًا.`,
    },
    startingCondition: {
      en: `${DEMO_EN} Placeholder description of the starting condition. The clinic will supply the real presenting findings.`,
      ar: `${DEMO_AR} وصف بديل للحالة قبل العلاج. ستوفّر العيادة النتائج الحقيقية عند الفحص.`,
    },
    diagnosis: {
      en: `${DEMO_EN} Placeholder diagnosis. A real case record would describe the clinical and radiographic assessment here.`,
      ar: `${DEMO_AR} تشخيص بديل. سيتضمّن السجل الحقيقي وصف الفحص الإكلينيكي والأشعة.`,
    },
    treatmentPlan: {
      en: `${DEMO_EN} Placeholder plan. A real record would set out the agreed plan and the alternatives discussed with the patient.`,
      ar: `${DEMO_AR} خطة بديلة. سيوضّح السجل الحقيقي الخطة المتفق عليها والبدائل التي نوقشت مع المريض.`,
    },
    steps: [
      {
        title: { en: `${DEMO_EN} Assessment and planning`, ar: `${DEMO_AR} الفحص والتخطيط` },
        body: {
          en: "Placeholder step text describing examination, imaging and planning.",
          ar: "نص بديل يوضّح الفحص والتصوير والتخطيط.",
        },
      },
      {
        title: { en: `${DEMO_EN} Implant placement`, ar: `${DEMO_AR} تركيب الزرعة` },
        body: {
          en: "Placeholder step text describing the placement appointment.",
          ar: "نص بديل يوضّح جلسة تركيب الزرعة.",
        },
      },
      {
        title: { en: `${DEMO_EN} Healing period`, ar: `${DEMO_AR} فترة الالتحام` },
        body: {
          en: "Placeholder step text describing the healing phase and review visits.",
          ar: "نص بديل يوضّح مرحلة الالتحام وزيارات المتابعة.",
        },
      },
      {
        title: { en: `${DEMO_EN} Fitting the crown`, ar: `${DEMO_AR} تركيب الطربوش` },
        body: {
          en: "Placeholder step text describing the restorative stage.",
          ar: "نص بديل يوضّح مرحلة التركيب النهائي.",
        },
      },
    ],
    result: {
      en: `${DEMO_EN} Placeholder outcome text. No real outcome is claimed. The clinic must supply the actual result, and no result may be presented as typical or guaranteed.`,
      ar: `${DEMO_AR} نص بديل للنتيجة. لا يتم الإدلاء بأي نتيجة حقيقية. على العيادة توفير النتيجة الفعلية، ولا يجوز تقديم أي نتيجة كأنها مضمونة أو معتادة.`,
    },
    images: {
      before: placeholderImage("before state", "قبل العلاج"),
      after: placeholderImage("after state", "بعد العلاج"),
    },
  },

  {
    slug: "demo-veneers-smile-makeover",
    provenance: "demo",
    service: "veneers",
    doctor: "demo-cosmetic-dentist",
    consent: "unknown",
    title: {
      en: `${DEMO_EN} Front-teeth appearance concerns treated with veneers`,
      ar: `${DEMO_AR} معالجة مظهر الأسنان الأمامية بالفينير`,
    },
    concern: {
      en: `${DEMO_EN} Illustrative example. A patient unhappy with the shape and colour of their upper front teeth.`,
      ar: `${DEMO_AR} مثال توضيحي. مريض غير راضٍ عن شكل ولون أسنانه الأمامية العلوية.`,
    },
    startingCondition: {
      en: `${DEMO_EN} Placeholder description of the starting condition.`,
      ar: `${DEMO_AR} وصف بديل للحالة قبل العلاج.`,
    },
    diagnosis: {
      en: `${DEMO_EN} Placeholder assessment. A real record would note enamel condition, gum health and bite factors.`,
      ar: `${DEMO_AR} تقييم بديل. سيوضّح السجل الحقيقي حالة المينا وصحة اللثة وعوامل الإطباق.`,
    },
    treatmentPlan: {
      en: `${DEMO_EN} Placeholder plan, including the mock-up/preview stage and the alternatives discussed.`,
      ar: `${DEMO_AR} خطة بديلة تتضمّن مرحلة النموذج التجريبي والبدائل التي نوقشت.`,
    },
    steps: [
      {
        title: { en: `${DEMO_EN} Consultation and smile design`, ar: `${DEMO_AR} الاستشارة وتصميم الابتسامة` },
        body: {
          en: "Placeholder step text describing assessment and design discussion.",
          ar: "نص بديل يوضّح التقييم ومناقشة التصميم.",
        },
      },
      {
        title: { en: `${DEMO_EN} Preview / mock-up`, ar: `${DEMO_AR} النموذج التجريبي` },
        body: {
          en: "Placeholder step text describing how the proposed result is previewed before committing.",
          ar: "نص بديل يوضّح كيف تُعرض النتيجة المقترحة قبل البدء.",
        },
      },
      {
        title: { en: `${DEMO_EN} Fitting`, ar: `${DEMO_AR} التركيب` },
        body: {
          en: "Placeholder step text describing the fitting appointment and adjustments.",
          ar: "نص بديل يوضّح جلسة التركيب والتعديلات.",
        },
      },
    ],
    result: {
      en: `${DEMO_EN} Placeholder outcome text. No real outcome is claimed.`,
      ar: `${DEMO_AR} نص بديل للنتيجة. لا يتم الإدلاء بأي نتيجة حقيقية.`,
    },
    images: {
      before: placeholderImage("before state", "قبل العلاج"),
      after: placeholderImage("after state", "بعد العلاج"),
    },
  },

  {
    slug: "demo-clear-aligner-crowding",
    provenance: "demo",
    service: "orthodontics",
    doctor: "demo-orthodontist",
    consent: "unknown",
    title: {
      en: `${DEMO_EN} Mild crowding treated with clear aligners`,
      ar: `${DEMO_AR} تزاحم بسيط في الأسنان بالتقويم الشفاف`,
    },
    concern: {
      en: `${DEMO_EN} Illustrative example. An adult patient wanting straighter front teeth without visible braces.`,
      ar: `${DEMO_AR} مثال توضيحي. مريض بالغ يرغب في تحسين انتظام أسنانه الأمامية دون تقويم ظاهر.`,
    },
    startingCondition: {
      en: `${DEMO_EN} Placeholder description of the starting alignment.`,
      ar: `${DEMO_AR} وصف بديل لانتظام الأسنان قبل العلاج.`,
    },
    diagnosis: {
      en: `${DEMO_EN} Placeholder assessment covering records, bite analysis and suitability for aligners.`,
      ar: `${DEMO_AR} تقييم بديل يشمل السجلات وتحليل الإطباق ومدى ملاءمة التقويم الشفاف.`,
    },
    treatmentPlan: {
      en: `${DEMO_EN} Placeholder plan. A real record would state the planned sequence and the retainer commitment afterwards.`,
      ar: `${DEMO_AR} خطة بديلة. سيوضّح السجل الحقيقي التسلسل المخطط والالتزام بالمثبت بعد العلاج.`,
    },
    steps: [
      {
        title: { en: `${DEMO_EN} Records and planning`, ar: `${DEMO_AR} السجلات والتخطيط` },
        body: {
          en: "Placeholder step text describing records taken and the treatment plan.",
          ar: "نص بديل يوضّح السجلات المأخوذة وخطة العلاج.",
        },
      },
      {
        title: { en: `${DEMO_EN} Aligner wear and reviews`, ar: `${DEMO_AR} ارتداء التقويم والمتابعة` },
        body: {
          en: "Placeholder step text describing wear-time expectations and review appointments.",
          ar: "نص بديل يوضّح مدة الارتداء المتوقعة وجلسات المتابعة.",
        },
      },
      {
        title: { en: `${DEMO_EN} Retention`, ar: `${DEMO_AR} التثبيت` },
        body: {
          en: "Placeholder step text describing retainers as an ongoing commitment, not an optional extra.",
          ar: "نص بديل يوضّح أن المثبت التزام مستمر وليس خيارًا إضافيًا.",
        },
      },
    ],
    result: {
      en: `${DEMO_EN} Placeholder outcome text. No real outcome is claimed, and aligner results depend heavily on patient wear compliance.`,
      ar: `${DEMO_AR} نص بديل للنتيجة. لا يتم الإدلاء بأي نتيجة حقيقية، ونتائج التقويم الشفاف تعتمد بدرجة كبيرة على التزام المريض بالارتداء.`,
    },
    images: {
      before: placeholderImage("before state", "قبل العلاج"),
      after: placeholderImage("after state", "بعد العلاج"),
    },
  },

  {
    slug: "demo-root-canal-molar",
    provenance: "demo",
    service: "root-canal-treatment",
    doctor: "demo-endodontist",
    consent: "unknown",
    title: {
      en: `${DEMO_EN} Saving a painful molar with root canal treatment`,
      ar: `${DEMO_AR} إنقاذ ضرس مؤلم بحشو العصب`,
    },
    concern: {
      en: `${DEMO_EN} Illustrative example. A patient with persistent toothache who wanted to avoid extraction if possible.`,
      ar: `${DEMO_AR} مثال توضيحي. مريض يعاني من ألم مستمر ويرغب في تجنّب الخلع إن أمكن.`,
    },
    startingCondition: {
      en: `${DEMO_EN} Placeholder description of the presenting symptoms and findings.`,
      ar: `${DEMO_AR} وصف بديل للأعراض والنتائج عند الفحص.`,
    },
    diagnosis: {
      en: `${DEMO_EN} Placeholder diagnosis. A real record would describe the pulp status and radiographic findings.`,
      ar: `${DEMO_AR} تشخيص بديل. سيوضّح السجل الحقيقي حالة العصب ونتائج الأشعة.`,
    },
    treatmentPlan: {
      en: `${DEMO_EN} Placeholder plan, including the root-canal-versus-extraction discussion and the crown that usually follows.`,
      ar: `${DEMO_AR} خطة بديلة تتضمّن المقارنة بين حشو العصب والخلع، والطربوش الذي يلي العلاج عادةً.`,
    },
    steps: [
      {
        title: { en: `${DEMO_EN} Diagnosis and pain relief`, ar: `${DEMO_AR} التشخيص وتخفيف الألم` },
        body: {
          en: "Placeholder step text describing the first visit.",
          ar: "نص بديل يوضّح الزيارة الأولى.",
        },
      },
      {
        title: { en: `${DEMO_EN} Root canal treatment`, ar: `${DEMO_AR} علاج العصب` },
        body: {
          en: "Placeholder step text describing the treatment appointments.",
          ar: "نص بديل يوضّح جلسات العلاج.",
        },
      },
      {
        title: { en: `${DEMO_EN} Restoring the tooth`, ar: `${DEMO_AR} ترميم الضرس` },
        body: {
          en: "Placeholder step text describing the final restoration.",
          ar: "نص بديل يوضّح الترميم النهائي.",
        },
      },
    ],
    result: {
      en: `${DEMO_EN} Placeholder outcome text. No real outcome, success rate, or longevity is claimed.`,
      ar: `${DEMO_AR} نص بديل للنتيجة. لا يتم الإدلاء بأي نتيجة أو نسبة نجاح أو مدة بقاء حقيقية.`,
    },
    images: {},
  },

  {
    slug: "demo-pediatric-first-visit",
    provenance: "demo",
    service: "pediatric-dentistry",
    doctor: "demo-pediatric-dentist",
    consent: "unknown",
    title: {
      en: `${DEMO_EN} A child's first dental visit`,
      ar: `${DEMO_AR} أول زيارة أسنان للطفل`,
    },
    concern: {
      en: `${DEMO_EN} Illustrative example. A parent worried that their child would be frightened by a first dental appointment.`,
      ar: `${DEMO_AR} مثال توضيحي. أحد الوالدين قلق من أن يخاف طفله من أول زيارة لطبيب الأسنان.`,
    },
    startingCondition: {
      en: `${DEMO_EN} Placeholder description of the child's dental condition at presentation.`,
      ar: `${DEMO_AR} وصف بديل لحالة أسنان الطفل عند أول زيارة.`,
    },
    diagnosis: {
      en: `${DEMO_EN} Placeholder assessment appropriate to a child's first examination.`,
      ar: `${DEMO_AR} تقييم بديل يناسب أول فحص للطفل.`,
    },
    treatmentPlan: {
      en: `${DEMO_EN} Placeholder plan focused on familiarisation, prevention and parental guidance.`,
      ar: `${DEMO_AR} خطة بديلة تركّز على التعريف بالعيادة والوقاية وإرشاد الوالدين.`,
    },
    steps: [
      {
        title: { en: `${DEMO_EN} Getting comfortable`, ar: `${DEMO_AR} كسر حاجز الخوف` },
        body: {
          en: "Placeholder step text describing how a nervous child is introduced to the clinic.",
          ar: "نص بديل يوضّح كيف يتم تعريف الطفل الخائف بالعيادة.",
        },
      },
      {
        title: { en: `${DEMO_EN} Examination`, ar: `${DEMO_AR} الفحص` },
        body: {
          en: "Placeholder step text describing a gentle first examination.",
          ar: "نص بديل يوضّح فحصًا أوليًا لطيفًا.",
        },
      },
      {
        title: { en: `${DEMO_EN} Prevention advice for parents`, ar: `${DEMO_AR} نصائح الوقاية للوالدين` },
        body: {
          en: "Placeholder step text describing brushing, diet and follow-up guidance.",
          ar: "نص بديل يوضّح إرشادات التنظيف والتغذية والمتابعة.",
        },
      },
    ],
    result: {
      en: `${DEMO_EN} Placeholder outcome text. No real outcome is claimed.`,
      ar: `${DEMO_AR} نص بديل للنتيجة. لا يتم الإدلاء بأي نتيجة حقيقية.`,
    },
    images: {},
  },
];

/** Case slugs that exist as content, for route generation. */
export const CASE_SLUGS: readonly string[] = CASES.map((c) => c.slug);

export function getCase(slug: string): ClinicalCase | undefined {
  return CASES.find((c) => c.slug === slug);
}
